// src/utils/biodataHelpers.js

/**
 * Helper function to check if edit permission is already granted for a biodata
 * @param {Object} biodata - The biodata object
 * @returns {boolean} - True if edit permission is granted, false otherwise
 */
export const hasEditPermission = (biodata) => {
  // Simplified check - just use the is_editable flag from database
  // The backend handles all the complex logic for when edit permission is valid
  return Boolean(biodata.is_editable);
};

/**
 * Helper function to get edit permission granted date
 * @param {Object} biodata - The biodata object
 * @returns {Date|null} - The date when edit permission was granted, or null if not granted
 */
export const getEditPermissionDate = (biodata) => {
  // Return the most recent edit permission date
  if (biodata.edit_permission_granted_at) {
    return new Date(biodata.edit_permission_granted_at);
  }
  if (biodata.last_edit_permission_at) {
    return new Date(biodata.last_edit_permission_at);
  }
  return null;
};

/**
 * Helper function to format biodata status for display
 * @param {string} status - The biodata status
 * @returns {Object} - Object with formatted status and styling
 */
export const formatBiodataStatus = (status) => {
  const statusMap = {
    'pending': { label: 'Pending', color: 'warning', icon: 'Clock' },
    'approved': { label: 'Approved', color: 'success', icon: 'CheckCircle' },
    'rejected': { label: 'Rejected', color: 'error', icon: 'XCircle' },
    'amended': { label: 'Amended', color: 'info', icon: 'Edit' },
    'draft': { label: 'Draft', color: 'secondary', icon: 'FileText' }
  };
  
  return statusMap[status] || { label: status, color: 'secondary', icon: 'FileText' };
};

/**
 * Helper function to calculate biodata age from date of birth
 * @param {string} dateOfBirth - The date of birth string
 * @returns {number|null} - Age in years, or null if invalid date
 */
export const calculateAge = (dateOfBirth) => {
  if (!dateOfBirth) return null;
  
  const today = new Date();
  const birthDate = new Date(dateOfBirth);
  
  if (isNaN(birthDate.getTime())) return null;
  
  let age = today.getFullYear() - birthDate.getFullYear();
  const monthDiff = today.getMonth() - birthDate.getMonth();
  
  if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
    age--;
  }
  
  return age;
};

/**
 * Helper function to format experience years for display
 * @param {number} years - Experience in years
 * @returns {string} - Formatted experience string
 */
export const formatExperience = (years) => {
  if (!years || years === 0) return 'No experience';
  if (years === 1) return '1 year';
  return `${years} years`;
};

/**
 * Helper function to validate passport number format
 * @param {string} passportNumber - The passport number
 * @returns {boolean} - True if valid format, false otherwise
 */
export const isValidPassportNumber = (passportNumber) => {
  if (!passportNumber) return false;
  // Basic validation: 6-9 alphanumeric characters
  const passportRegex = /^[A-Z0-9]{6,9}$/i;
  return passportRegex.test(passportNumber.replace(/\s/g, ''));
};

/**
 * Helper function to get biodata completion percentage
 * @param {Object} biodata - The biodata object
 * @returns {number} - Completion percentage (0-100)
 */
export const getBiodataCompletionPercentage = (biodata) => {
  const requiredFields = [
    'full_name', 'nationality', 'passport_number', 'date_of_birth',
    'gender', 'education_level', 'experience_years', 'medical_status'
  ];
  
  const completedFields = requiredFields.filter(field => 
    biodata[field] && biodata[field].toString().trim() !== ''
  ).length;
  
  return Math.round((completedFields / requiredFields.length) * 100);
};